import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useStock } from '../hooks/useStock';

// ── Live animated right panel for hero ─────────────────────────
const LiveCards = ({ scrollTo }) => {
  const NAMES = ['3 Month Boosts','1 Month Boosts','Lifetime Boosts','Token Pack'];
  const QTYS  = ['× 1','× 2','× 5','× 10'];
  const PRICES= ['$6.99','$13.98','$9.95','$19.90','$34.99'];
  const mkOrder = (offset=0) => ({
    id: '#' + (4819 + offset),
    prod: NAMES[offset % NAMES.length] + ' ' + QTYS[offset % QTYS.length],
    price: PRICES[offset % PRICES.length],
    status: offset % 4 === 2 ? 'Pending' : 'Completed',
    fresh: false
  });

  const [orders, setOrders] = React.useState([mkOrder(2), mkOrder(1), mkOrder(0)]);
  const [counter, setCounter] = React.useState(3);

  React.useEffect(() => {
    const t = setInterval(() => {
      setCounter(n => {
        const id = n;
        const prod = NAMES[id % NAMES.length] + ' ' + QTYS[id % QTYS.length];
        const price = PRICES[id % PRICES.length];
        const newRow = { id: '#' + (4819 + id), prod, price, status: 'Completed', fresh: true };
        setOrders(prev => [newRow, ...prev.slice(0, 2)]);
        setTimeout(() => setOrders(prev => prev.map((o,i) => i===0 ? {...o, fresh:false} : o)), 600);
        return n + 1;
      });
    }, 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="os-hc-panel">
      {/* Top row: 2 feature cards */}
      <div className="os-hc-top2">
        <div className="os-hc-fcard">
          <div className="os-hc-fcard-icon" style={{background:'rgba(167,139,250,0.12)',color:'#a78bfa'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          </div>
          <div>
            <div className="os-hc-fcard-title">Automated delivery</div>
            <div className="os-hc-fcard-sub">Delivered in seconds after checkout.</div>
          </div>
        </div>
        <div className="os-hc-fcard">
          <div className="os-hc-fcard-icon" style={{background:'rgba(167,139,250,0.12)',color:'#a78bfa'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div>
            <div className="os-hc-fcard-title">Protected checkout</div>
            <div className="os-hc-fcard-sub">Secure payments and privacy-first flow.</div>
          </div>
        </div>
      </div>

      {/* Recommended card */}
      <div className="os-hc-rec" onClick={() => scrollTo('products')}>
        <div>
          <div className="os-hc-rec-label">RECOMMENDED</div>
          <div className="os-hc-rec-title">Start with a Boost Package</div>
          <div className="os-hc-rec-sub">Most popular choice for fast results — delivered instantly.</div>
        </div>
        <button className="os-hc-rec-btn">
          View
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
      </div>

      {/* Live orders feed */}
      <div className="os-hc-live">
        <div className="os-hc-live-hdr">
          <span className="os-hc-live-title">Live Orders</span>
          <span className="os-hc-live-dot">● Live</span>
        </div>
        {orders.map((o, i) => (
          <div key={o.id + i} className={`os-hc-live-row${o.fresh ? ' fresh' : ''}`}>
            <span className="os-hc-live-id">{o.id}</span>
            <span className="os-hc-live-prod">{o.prod}</span>
            <span className="os-hc-live-price">{o.price}</span>
            <span className={`os-hc-live-badge ${o.status === 'Completed' ? 'done' : 'pend'}`}>{o.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


const Home = () => {
  const { stock: products, lastUpdated, loading } = useStock(3000);
  const [activeTab, setActiveTab] = useState('boost_3m');
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/reviews').then(r => r.json()).then(setReviews).catch(() => {});
    if (window.trackFunnel) window.trackFunnel('page_visit', { page: 'home' });
  }, []);

  const filteredProducts = (products || []).filter(p => 
    activeTab === 'all' ? true : activeTab === 'tools' ? p.type === 'tool' : p.id === activeTab
  );

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="os-hero os-hero-card">
        <div className="os-hc-inner">

          {/* LEFT — text */}
          <div className="os-hc-left">
            <div className="os-hc-badge animate-drop">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              Ready when you are
            </div>

            <h1 className="os-hc-title animate-drop">
              Upgrade your{' '}
              <span className="os-hc-purple">Discord</span><br/>
              <span className="os-hc-pink">experience</span> today.
            </h1>

            <p className="os-hc-sub animate-drop delay-1">
              Instant delivery, secure checkout, and support that actually
              replies. Pick a service, checkout, and you're done.
            </p>

            <div className="os-hc-btns animate-drop delay-2">
              <button className="os-hc-btn-fill" onClick={() => scrollTo('products')}>
                Shop products
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
              <a href="https://discord.gg/outsourced" target="_blank" rel="noreferrer" className="os-hc-btn-ghost">
                Join Discord
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
              </a>
            </div>

            <div className="os-hc-pills animate-drop delay-3">
              <span className="os-hc-pill"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>Instant delivery</span>
              <span className="os-hc-pill"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>Secure payments</span>
              <span className="os-hc-pill"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>24/7 support</span>
            </div>
          </div>

          {/* RIGHT — live animated cards */}
          <div className="os-hc-right animate-drop delay-1">
            <LiveCards scrollTo={scrollTo} />
          </div>

        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="os-section" id="features">
        <div className="os-container">
          <h2 className="os-heading">Why <span>Outsourced</span> works better</h2>
          <p className="os-subheading">Fast delivery, flexible payments, and real support — built for people who want results, not delays.</p>
          
          <div className="os-features-grid">
            <div className="os-fcard">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <h3>Convenient Payment Options</h3>
              <p>Pay with major cryptocurrencies, PayPal, credit/debit cards, or gift cards — whatever fits you best.</p>
            </div>
            <div className="os-fcard highlighted">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </div>
              <h3>Competitive Pricing</h3>
              <p>High-quality services without the high price. Premium features at affordable rates.</p>
            </div>
            <div className="os-fcard">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <h3>Fully Legal & Safe</h3>
              <p>Security and compliance come first, so you can purchase with peace of mind.</p>
            </div>
            <div className="os-fcard">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <h3>24/7 Support</h3>
              <p>Our team is available around the clock to assist with any inquiries or issues.</p>
            </div>
            <div className="os-fcard">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <h3>Reputation You Can Trust</h3>
              <p>Excellent reviews and consistent delivery — trusted by returning customers.</p>
            </div>
            <div className="os-fcard">
              <div className="os-fcard-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </div>
              <h3>Instant Service Delivery</h3>
              <p>Fast, seamless delivery with quick activation — typically under 30 seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCTS ===== */}
      <section className="os-section" id="products">
        <div className="os-container">
          <h2 className="os-heading">Choose your <span>product</span></h2>
          <p className="os-subheading">Pick what fits your goals. Checkout is quick, secure, and supports multiple payment methods.</p>

          <div className="os-tabs">
            <div className="os-tabs-inner">
              <button 
                className={`os-tab ${activeTab === 'boost_3m' ? 'active' : ''}`} 
                onClick={() => { setActiveTab('boost_3m'); if(window.trackFunnel) window.trackFunnel('product_view',{product:'boost_3m'}); }}
              >
                3 Month Boosts
              </button>
              <button 
                className={`os-tab ${activeTab === 'boost_1m' ? 'active' : ''}`} 
                onClick={() => { setActiveTab('boost_1m'); if(window.trackFunnel) window.trackFunnel('product_view',{product:'boost_1m'}); }}
              >
                1 Month Boosts
              </button>
              {(products || []).filter(p => p.type === 'token').map(p => (
                <button 
                  key={p.id}
                  className={`os-tab ${activeTab === p.id ? 'active' : ''}`} 
                  onClick={() => { setActiveTab(p.id); if(window.trackFunnel) window.trackFunnel('product_view',{product:p.id}); }}
                >
                  {p.product}
                </button>
              ))}
              {(products || []).some(p => p.type === 'tool') && (
                <button 
                  className={`os-tab ${activeTab === 'tools' ? 'active' : ''}`} 
                  onClick={() => { setActiveTab('tools'); if(window.trackFunnel) window.trackFunnel('product_view',{product:'tools'}); }}
                >
                  Tools
                </button>
              )}
              <button 
                className={`os-tab ${activeTab === 'all' ? 'active' : ''}`} 
                onClick={() => setActiveTab('all')}
              >
                All Products
              </button>
            </div>
          </div>

          {lastUpdated && (
            <div className="os-stock-live">
              <span className="os-live-dot"></span>
              Live stock — updated {new Date(lastUpdated).toLocaleTimeString()}
            </div>
          )}

          <div className={`os-products ${(activeTab === 'all' || activeTab === 'tools') ? 'os-products-duo' : 'os-products-solo'}`}>
            {loading ? (
              <div className="os-loading">
                <div className="loading-spinner large"></div>
                <p>Loading products...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} isPopular={index === 0 && activeTab === 'all'} />
              ))
            ) : (
              <div className="os-loading"><p>No products found</p></div>
            )}
          </div>
        </div>
      </section>

      {/* ===== AUTO-REBOOST CTA ===== */}
      <section className="os-section" id="auto-reboost">
        <div className="os-container">
          <h2 className="os-heading">Never lose your <span>boosts</span></h2>
          <p className="os-subheading">Set it and forget it — Auto-Reboost keeps your server boosted around the clock.</p>

          <div className="os-reboost-grid">
            <div className="os-reboost-card">
              <div className="os-reboost-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              </div>
              <h3>Automatic Renewals</h3>
              <p>Your boosts renew automatically when they expire — no manual action needed.</p>
            </div>
            <div className="os-reboost-card os-reboost-card-highlight">
              <div className="os-reboost-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <h3>Save Up to 25%</h3>
              <p>Pay upfront for quarterly or annual plans and save big on every renewal cycle.</p>
            </div>
            <div className="os-reboost-card">
              <div className="os-reboost-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <h3>Flexible Billing</h3>
              <p>Choose upfront for discounts or pay-as-you-go from your balance each month.</p>
            </div>
          </div>

          {/* Auto-Reboost Promo Banner */}
          <div className="os-reboost-promo" style={{ marginTop: '2.5rem' }}>
            <div className="os-reboost-promo-glow"></div>
            <div className="os-reboost-promo-inner">
              <div className="os-reboost-promo-left">
                <div className="os-reboost-promo-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                </div>
                <div>
                  <div className="os-reboost-promo-title">Auto-Reboost</div>
                  <div className="os-reboost-promo-desc">Set it and forget it — boosts renew automatically so your server never drops a level.</div>
                  <div className="os-reboost-promo-perks">
                    <span className="os-reboost-perk">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      Save up to 25%
                    </span>
                    <span className="os-reboost-perk">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      Cancel anytime
                    </span>
                    <span className="os-reboost-perk">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      Pay-as-you-go option
                    </span>
                  </div>
                </div>
              </div>
              <button className="os-reboost-promo-btn" onClick={() => {
                const user = localStorage.getItem('user');
                if (user) {
                  navigate('/dashboard');
                  setTimeout(() => window.dispatchEvent(new CustomEvent('nav-tab', { detail: 'reboost' })), 100);
                } else {
                  navigate('/login');
                }
              }}>
                Set Up Auto-Reboost
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="os-section" id="process">
        <div className="os-container">
          <h2 className="os-heading">Our <span>process</span></h2>
          <p className="os-subheading">Simple, fast, and transparent from purchase to delivery.</p>
          
          <div className="os-steps">
            <div className="os-step">
              <div className="os-step-num accent">Step 1</div>
              <h3>Select Your Product</h3>
              <p>Pick the perfect package that matches your needs and goals for boosting your server.</p>
            </div>
            <div className="os-step">
              <div className="os-step-num accent">Step 2</div>
              <h3>Complete Your Purchase</h3>
              <p>Use our secure checkout process to finalize your order and make your payment.</p>
            </div>
            <div className="os-step">
              <div className="os-step-num accent">Step 3</div>
              <h3>Immediate Activation</h3>
              <p>Once your payment is confirmed, your server boosts will be instantly delivered and ready to use.</p>
            </div>
            <div className="os-step">
              <div className="os-step-num accent">Step 4</div>
              <h3>Unlock New Features</h3>
              <p>Enjoy your boosted server with new capabilities and perks that enhance your community experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== REVIEWS ===== */}
      <section className="os-section" id="reviews">
        <div className="os-container">
          {/* Reviews Header */}
          <div className="os-reviews-header">
            <div className="os-reviews-title">
              <span className="os-reviews-icon">☆</span>
              <div>
                <h2>Reviews</h2>
                <p>Hear from our customers</p>
              </div>
            </div>
            <div className="os-reviews-rating">
              <span className="os-rating-stars">{'★'.repeat(Math.round(reviews.length > 0 ? reviews.reduce((a, r) => a + (r.stars || 5), 0) / reviews.length : 5))}</span>
              <span className="os-rating-score">
                {reviews.length > 0 ? (reviews.reduce((a, r) => a + (r.stars || 5), 0) / reviews.length).toFixed(1) : '5.0'}
              </span>
              <span className="os-rating-count">({reviews.length})</span>
            </div>
          </div>
          
          {/* Reviews Grid */}
          <div className="os-reviews-grid">
            {reviews.slice(0, 6).map((r, i) => (
              <div className="os-review-card" key={r.id || i}>
                <div className="os-review-card-stars">
                  {'★'.repeat(r.stars || 5)}{'☆'.repeat(5 - (r.stars || 5))}
                </div>
                <p className="os-review-card-text">{r.text}</p>
                <div className="os-review-card-footer">
                  <div className="os-review-card-info">
                    <span className="os-review-card-author">{r.name || 'Anonymous'}</span>
                    <span className="os-review-card-date">
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB').replace(/\//g, '. ') : ''}
                    </span>
                  </div>
                  <span className="os-review-card-badge">Outsourced</span>
                </div>
              </div>
            ))}
            {reviews.length === 0 && (
              <div style={{textAlign:'center',color:'var(--text-muted)',padding:'2rem',gridColumn:'1/-1'}}>Loading reviews...</div>
            )}
          </div>
          
          {/* View All Button */}
          <div className="os-reviews-footer">
            <Link to="/reviews" className="os-view-all-btn">
              View all reviews
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SUPPORT ===== */}
      <section className="os-section" id="support">
        <div className="os-container">
          <div className="os-hc-inner" style={{alignItems:'start'}}>

            {/* LEFT text col */}
            <div className="os-hc-left">
              <div className="os-hc-badge animate-drop">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Support
              </div>
              <h2 className="os-hc-title animate-drop" style={{fontSize:'clamp(2rem,3.5vw,3rem)'}}>
                Need <span className="os-hc-purple">help?</span>
              </h2>
              <p className="os-hc-sub animate-drop delay-1">
                Get support fast — open a ticket on Discord, or use Telegram for quick updates and messages.
              </p>
              <div className="os-hc-pills animate-drop delay-2">
                <span className="os-hc-pill" style={{color:'var(--accent)'}}>● Online now</span>
                <span className="os-hc-pill">2,400+ members</span>
                <span className="os-hc-pill">Fast response</span>
              </div>
            </div>

            {/* RIGHT — same card panel style as hero */}
            <div className="os-hc-right animate-drop delay-1">
              <div className="os-hc-panel">
                {/* Top 2 cards */}
                <div className="os-hc-top2">
                  <div className="os-hc-fcard">
                    <div className="os-hc-fcard-icon" style={{background:'rgba(88,101,242,0.15)',color:'#5865f2'}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                    </div>
                    <div>
                      <div className="os-hc-fcard-title">Discord Support</div>
                      <div className="os-hc-fcard-sub">Open a ticket, get order help, fast replies from staff.</div>
                    </div>
                  </div>
                  <div className="os-hc-fcard">
                    <div className="os-hc-fcard-icon" style={{background:'rgba(0,136,204,0.15)',color:'#0af'}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                    </div>
                    <div>
                      <div className="os-hc-fcard-title">Telegram</div>
                      <div className="os-hc-fcard-sub">Updates, announcements, quick replies anytime.</div>
                    </div>
                  </div>
                </div>

                {/* Join recommended card */}
                <div className="os-hc-rec">
                  <div>
                    <div className="os-hc-rec-label">RECOMMENDED</div>
                    <div className="os-hc-rec-title">Join our Discord server</div>
                    <div className="os-hc-rec-sub">Open a ticket, get order help, and receive fast replies — or just hang out with the community.</div>
                  </div>
                  <a href="https://discord.gg/outsourced" target="_blank" rel="noreferrer" className="os-hc-rec-btn" style={{textDecoration:'none'}}>
                    Join
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </a>
                </div>

                {/* Telegram link */}
                <a href="https://t.me/outsourced" target="_blank" rel="noreferrer" className="os-hc-fcard" style={{textDecoration:'none',justifyContent:'space-between',cursor:'pointer'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="os-hc-fcard-icon" style={{background:'rgba(0,136,204,0.1)',color:'#0af',flexShrink:0}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/></svg>
                    </div>
                    <div>
                      <div className="os-hc-fcard-title">Open Telegram</div>
                      <div className="os-hc-fcard-sub">For quick messages and announcements.</div>
                    </div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{color:'var(--text-muted)',flexShrink:0}}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
